import React, { useState, useEffect, useRef, useMemo } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, RotateCcw } from 'lucide-react';

const YearlyCalendarView = ({ userId, onEventClick, localEvents = [], nylasEvents = [], outlookEvents = [] }) => {
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [allEvents, setAllEvents] = useState([]);
  const transformRef = useRef(null);

  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Combine local, Nylas and Outlook/Calendar.app events
  useEffect(() => {
    const combined = [];
    
    localEvents.forEach(event => {
      combined.push({
        ...event,
        source: 'local',
        color: event.color,
        category: event.category || event.type || 'other'
      });
    });
    
    nylasEvents.forEach(event => {
      combined.push({
        ...event,
        source: 'nylas',
        color: event.color || 'purple',
        category: event.category || 'meeting'
      });
    });

    // Outlook events arrive with ISO `start` / `start_time` — derive
    // local day/month/year so getEventsForDay (which keys on those fields
    // for `source === 'local'`) AND the start_time-based path both match.
    outlookEvents.forEach(event => {
      const startIso = event.start_time || event.start;
      if (!startIso) return;
      const d = new Date(startIso);
      if (isNaN(d.getTime())) return;
      combined.push({
        ...event,
        source: 'outlook',
        color: event.color,
        category: event.category || event.type || 'other',
        title: event.title || event.subject || event.name || 'Event',
        start_time: startIso,
        day: d.getDate(),
        month: d.getMonth(),
        year: d.getFullYear(),
      });
    });
    
    setAllEvents(combined);
  }, [localEvents, nylasEvents, outlookEvents]);

  // Get days in a month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get day of week for a specific date (0 = Sunday)
  const getDayOfWeek = (month, day, year) => {
    return new Date(year, month, day).getDay();
  };

  // Check if a day is today
  const isToday = (month, day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === month && 
           today.getFullYear() === currentYear;
  };

  // Get events for a specific day
  const getEventsForDay = (month, day) => {
    return allEvents.filter(event => {
      if (event.day === day && event.month === month && event.year === currentYear) {
        return true;
      }
      if (event.start_time) {
        let eventDate;
        if (typeof event.start_time === 'number') {
          eventDate = new Date(event.start_time * 1000);
        } else {
          eventDate = new Date(event.start_time);
        }
        return eventDate.getDate() === day && 
               eventDate.getMonth() === month && 
               eventDate.getFullYear() === currentYear;
      }
      return false;
    });
  };

  // Navigate years
  const handlePrevYear = () => setCurrentYear(prev => prev - 1);
  const handleNextYear = () => setCurrentYear(prev => prev + 1);

  // Zoom controls
  const handleZoomIn = () => transformRef.current?.zoomIn();
  const handleZoomOut = () => transformRef.current?.zoomOut();
  const handleResetTransform = () => transformRef.current?.resetTransform();
  const handleCenterView = () => transformRef.current?.centerView();

  // Generate the year planner grid data
  const yearData = useMemo(() => {
    return months.map((monthName, monthIndex) => {
      const daysInMonth = getDaysInMonth(monthIndex, currentYear);
      const days = [];
      
      for (let day = 1; day <= 31; day++) {
        if (day <= daysInMonth) {
          const dayOfWeek = getDayOfWeek(monthIndex, day, currentYear);
          const events = getEventsForDay(monthIndex, day);
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          days.push({
            day,
            dayName: dayNames[dayOfWeek],
            isWeekend,
            isToday: isToday(monthIndex, day),
            events,
            valid: true
          });
        } else {
          days.push({ day, valid: false });
        }
      }
      
      return { monthName, monthIndex, days };
    });
  }, [currentYear, allEvents]);

  return (
    <div className="h-full flex flex-col bg-gray-100" data-testid="yearly-calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-300 flex-shrink-0 bg-white shadow-sm">
        <div className="flex items-center gap-4">
          <button 
            onClick={handlePrevYear}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
            data-testid="yearly-prev-year"
          >
            <ChevronLeft size={20} className="text-gray-700" />
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-wide">
            {currentYear} YEAR PLANNER
          </h1>
          <button 
            onClick={handleNextYear}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
            data-testid="yearly-next-year"
          >
            <ChevronRight size={20} className="text-gray-700" />
          </button>
          <button
            onClick={() => setCurrentYear(new Date().getFullYear())}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors border border-gray-300 font-medium"
            data-testid="yearly-today-btn"
          >
            Today
          </button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs hidden md:block mr-2 bg-gray-100 px-3 py-1.5 rounded-full">
            Scroll to zoom • Drag to pan
          </span>
          <button
            onClick={handleZoomOut}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
            title="Zoom Out"
            data-testid="yearly-zoom-out"
          >
            <ZoomOut size={18} className="text-gray-700" />
          </button>
          <button
            onClick={handleZoomIn}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
            title="Zoom In"
            data-testid="yearly-zoom-in"
          >
            <ZoomIn size={18} className="text-gray-700" />
          </button>
          <button
            onClick={handleResetTransform}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
            title="Reset View"
            data-testid="yearly-reset-view"
          >
            <RotateCcw size={18} className="text-gray-700" />
          </button>
          <button
            onClick={handleCenterView}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300"
            title="Center View"
            data-testid="yearly-center-view"
          >
            <Maximize2 size={18} className="text-gray-700" />
          </button>
        </div>
      </div>

      {/* Zoomable/Pannable Year Planner */}
      <div className="flex-1 overflow-hidden bg-gray-200">
        <TransformWrapper
          ref={transformRef}
          initialScale={0.5}
          minScale={0.2}
          maxScale={2}
          centerOnInit={true}
          wheel={{ step: 0.05 }}
          doubleClick={{ mode: 'reset' }}
          panning={{ velocityDisabled: false }}
        >
          {() => (
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                justifyContent: 'center',
                padding: '20px'
              }}
            >
              <div className="bg-white shadow-xl rounded-lg overflow-hidden border border-gray-300">
                {/* Year Planner Table */}
                <table className="border-collapse" style={{ minWidth: '2800px' }}>
                  {/* Header Row - Day Numbers */}
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-10 bg-gray-700 text-white font-bold text-sm p-2 border border-gray-400 w-20">
                        {currentYear}
                      </th>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <th 
                          key={day} 
                          className="bg-gray-200 text-gray-700 font-bold text-sm p-2 border border-gray-400 text-center min-w-[85px]"
                        >
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  
                  {/* Month Rows */}
                  <tbody>
                    {yearData.map(({ monthName, monthIndex, days }) => (
                      <tr key={monthName} data-testid={`yearly-month-row-${monthIndex}`}>
                        {/* Month Label */}
                        <td 
                          className="sticky left-0 z-10 bg-gray-700 text-white font-bold text-sm p-3 border border-gray-400 text-center"
                          style={{ width: '80px' }}
                        >
                          {monthName}
                        </td>
                        
                        {/* Day Cells */}
                        {days.map((dayData, dayIndex) => {
                          if (!dayData.valid) {
                            return (
                              <td 
                                key={dayIndex} 
                                className="border border-gray-300 bg-gray-100 min-w-[85px]"
                                style={{ height: '80px' }}
                              />
                            );
                          }
                          
                          const hasEvents = dayData.events.length > 0;
                          const firstEvent = hasEvents ? dayData.events[0] : null;
                          
                          // Get category color for the cell
                          const getCategoryBgColor = (event) => {
                            if (!event) return null;
                            const category = event.category || 'other';
                            const colorMap = {
                              'meeting': { bg: '#dbeafe', header: '#93c5fd', text: '#1e40af' },      // blue
                              'work': { bg: '#dbeafe', header: '#93c5fd', text: '#1e40af' },         // blue
                              'cresa': { bg: '#d1fae5', header: '#6ee7b7', text: '#065f46' },        // emerald
                              'birthday': { bg: '#fce7f3', header: '#f9a8d4', text: '#9d174d' },     // pink
                              'health': { bg: '#fee2e2', header: '#fca5a5', text: '#991b1b' },       // red
                              'sports': { bg: '#ffedd5', header: '#fdba74', text: '#9a3412' },       // orange
                              'religious': { bg: '#ede9fe', header: '#c4b5fd', text: '#5b21b6' },    // purple
                              'travel': { bg: '#cffafe', header: '#67e8f9', text: '#0e7490' },       // cyan
                              'home': { bg: '#dcfce7', header: '#86efac', text: '#166534' },         // green
                              'social': { bg: '#fef9c3', header: '#fde047', text: '#854d0e' },       // yellow
                              'education': { bg: '#e0e7ff', header: '#a5b4fc', text: '#3730a3' },    // indigo
                              'networking': { bg: '#ccfbf1', header: '#5eead4', text: '#0f766e' },   // teal
                              'other': { bg: '#f3f4f6', header: '#d1d5db', text: '#374151' },        // gray
                            };
                            return colorMap[category] || colorMap['other'];
                          };
                          
                          const eventColors = getCategoryBgColor(firstEvent);
                          
                          // Weekend cells get blue background, event cells get category color
                          let cellBgColor = '#ffffff';
                          let headerBgColor = '#f3f4f6';
                          let textColor = '#374151';
                          
                          if (hasEvents && eventColors) {
                            cellBgColor = eventColors.bg;
                            headerBgColor = eventColors.header;
                            textColor = eventColors.text;
                          } else if (dayData.isWeekend) {
                            cellBgColor = '#dbeafe';
                            headerBgColor = '#bfdbfe';
                          }
                          
                          return (
                            <td
                              key={dayIndex}
                              onClick={() => {
                                if (hasEvents && onEventClick) {
                                  onEventClick(dayData.events[0], dayData.day, monthIndex, currentYear);
                                }
                              }}
                              className={`border border-gray-400 p-0 min-w-[85px] align-top cursor-pointer transition-all
                                ${dayData.isToday ? 'ring-2 ring-blue-600 ring-inset' : ''}
                                ${hasEvents ? 'hover:opacity-90' : 'hover:bg-gray-50'}
                              `}
                              style={{ 
                                height: '80px',
                                backgroundColor: cellBgColor
                              }}
                              title={hasEvents ? `${dayData.events.length} event(s) - Click to view` : `${dayData.dayName} ${dayData.day}`}
                            >
                              {/* Header bar with day name and number */}
                              <div 
                                className="border-b border-gray-400 px-2 py-1 text-center"
                                style={{ backgroundColor: headerBgColor }}
                              >
                                <span 
                                  className={`text-sm font-semibold`}
                                  style={{ color: dayData.isToday ? '#2563eb' : (hasEvents ? textColor : '#374151') }}
                                >
                                  {dayData.dayName} {dayData.day}
                                </span>
                              </div>
                              
                              {/* Event area */}
                              <div className="p-1.5 overflow-hidden" style={{ height: 'calc(100% - 28px)' }}>
                                {hasEvents && (
                                  <div className="flex flex-col gap-1">
                                    {dayData.events.slice(0, 2).map((event, idx) => (
                                      <div 
                                        key={idx}
                                        className="text-sm font-bold leading-tight truncate"
                                        style={{ color: textColor }}
                                        title={event.title || event.name}
                                      >
                                        {event.title || event.name || 'Event'}
                                      </div>
                                    ))}
                                    {dayData.events.length > 2 && (
                                      <div 
                                        className="text-xs font-medium"
                                        style={{ color: textColor, opacity: 0.7 }}
                                      >
                                        +{dayData.events.length - 2} more
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TransformComponent>
          )}
        </TransformWrapper>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 py-2 border-t border-gray-300 bg-white flex-shrink-0 flex-wrap px-4">
        <span className="text-xs text-gray-600 font-semibold">Categories:</span>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}></span>
          Meeting/Work
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#d1fae5', border: '1px solid #6ee7b7' }}></span>
          Cresa
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#fce7f3', border: '1px solid #f9a8d4' }}></span>
          Birthday
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#fee2e2', border: '1px solid #fca5a5' }}></span>
          Health
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#ffedd5', border: '1px solid #fdba74' }}></span>
          Sports
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#ede9fe', border: '1px solid #c4b5fd' }}></span>
          Religious
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#cffafe', border: '1px solid #67e8f9' }}></span>
          Travel
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="px-2 py-0.5 text-blue-600 font-bold text-[10px] ring-2 ring-blue-600 rounded bg-white">27</span>
          Today
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <span className="w-4 h-4 rounded" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}></span>
          Weekend
        </div>
      </div>
    </div>
  );
};

export default YearlyCalendarView;
